import { Link, useNavigate } from "@tanstack/react-router";
import React, { useState } from "react";
import { JobOfferForm } from "@/features/job-offer/forms/job-offer-form";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Eye, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import {
  JobOfferData,
  OfferWageSource,
  detectUserTimezone,
} from "../utils/job-offer-data";
import { useActiveOrganizationWageSets } from "@/features/organization-wage-sets/hooks/use-active-organization-wage-sets";
import {
  customWages,
  withOrganizationWageSetPreview,
} from "../utils/organization-wage-set-preview";
import { JobOfferCard } from "../matching/job-offer-card";
import { useOrganization } from "@/hooks/use-organization";
import { useOrgPath } from "@/hooks/useOrgPath";
import { WageFrequency } from "@api/shared-runtime/enums";

export function JobOffer({
  jobId,
  defaultSchedules,
  variant = "page",
  onClose,
  onCompleted,
}: {
  jobId?: string;
  defaultSchedules?: JobOfferData["schedules"];
  variant?: "page" | "dialog";
  onClose?: () => void;
  onCompleted?: () => void;
}) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const navigate = useNavigate();
  const orgPath = useOrgPath();
  const closeDestination = React.useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    navigate({
      to: orgPath("/jobs/offer"),
    });
  }, [navigate, onClose]);
  const handleAfterSuccess = React.useCallback(() => {
    onCompleted?.();
    if (!onCompleted || !onClose) {
      closeDestination();
    }
  }, [closeDestination, onClose, onCompleted]);
  const heading = jobId ? "Edit Job Offer" : "Create Job Offer";
  const jobMutation = useMutation(
    trpc.jobOffer.create.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.jobOffer.organizationGetAll.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.jobOffer.getForCard.queryKey({
              id: jobId ?? "",
            }),
          }),
        ]);
        handleAfterSuccess();
      },
    }),
  );

  const [formData, setFormData] = useState<JobOfferData>({
    title: "",
    // @ts-expect-error - existing dynamic route typing
    facilityId: undefined,
    // Wage rates
    wageSource: OfferWageSource.Custom,
    organizationWageSetId: null,
    regularWage: "",
    regularOvertimeWage: "",
    weekendWage: "",
    weekendOvertimeWage: "",
    onCallWage: "",
    onCallOvertimeWage: "",
    onCallCallbackWage: "",
    onCallCallbackOvertimeWage: "",
    holidayWage: "",
    holidayOvertimeWage: "",
    wageFrequencyId: WageFrequency.Hourly,
    timezoneId: detectUserTimezone(),
    schedules: defaultSchedules ?? [],
    description: "",
    decision: null,
    otherUserAccepted: null,
    canceledByOrganizationBusinessUnitAt: null,
  });
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);

  const handleFormUpdate = ({ data }: { data: JobOfferData }) => {
    setFormData(data);
  };

  const handleSave = async ({
    data,
  }: {
    data: JobOfferData;
    isDraft: boolean;
  }) => {
    const isPerShift = data.wageFrequencyId === WageFrequency.OneTime;
    const wages =
      data.wageSource === OfferWageSource.OrganizationWageSet &&
      data.organizationWageSetId
        ? { organizationWageSetId: data.organizationWageSetId }
        : customWages(data, isPerShift);

    await jobMutation.mutateAsync({
      description: data.description || "",
      facilityId: data.facility.id,
      title: data.title || undefined,
      organizationBusinessUnitId: selectedOrgBusinessUnit?.id,
      ...wages,
      timezoneId: data.timezoneId,
      schedules: data.schedules,
      shareWithProviderPoolIds: data.providerPools?.map((pool) => pool.id),
      shareWithUserIds: data.providerUsers?.map((user) => user.id),
      expiresInDuration:
        data.expiresInDuration && data.expiresInDurationUnit
          ? data.expiresInDuration
          : undefined,
      expiresInDurationUnit:
        data.expiresInDuration && data.expiresInDurationUnit
          ? data.expiresInDurationUnit
          : undefined,
    });
  };

  const { data: organizationWageSets } = useActiveOrganizationWageSets(
    selectedOrgBusinessUnit.id,
  );
  const previewOffer = withOrganizationWageSetPreview({
    formData,
    organizationWageSets,
  });
  return (
    <div
      className={
        variant === "page"
          ? "container mx-auto py-4 px-4 sm:px-6 lg:py-6"
          : "py-2 sm:py-4 px-1 sm:px-2"
      }
    >
      <div className="mb-4 flex items-center justify-between">
        {variant === "page" && (
          <Link to={orgPath("/jobs/offer")} className="flex items-center">
            <ArrowLeft size={16} className="mr-1.5" />
            Back to Dashboard
          </Link>
        )}
      </div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        {variant === "page" && (
          <h1 className="text-2xl font-bold text-gray-900">{heading}</h1>
        )}
      </div>

      {/* Mobile Preview Dialog Button */}
      <div className="lg:hidden">
        <Dialog
          open={isPreviewDialogOpen}
          onOpenChange={setIsPreviewDialogOpen}
        >
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="flex items-center gap-2"
              disabled={!formData}
            >
              <Eye className="h-4 w-4" />
              Preview
            </Button>
          </DialogTrigger>
          <DialogContent
            showCloseButton={false}
            className="p-0 gap-0 sm:max-w-[520px] w-[calc(100vw-2rem)] max-h-[90vh] overflow-hidden rounded-lg"
          >
            <DialogHeader className="p-4 bg-gray-50 border-b flex-row items-center justify-between space-y-0">
              <div className="flex items-center gap-2">
                <DialogTitle className="font-semibold text-gray-800 text-base">
                  Preview
                </DialogTitle>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Live
                </span>
              </div>
              <button
                type="button"
                onClick={() => setIsPreviewDialogOpen(false)}
                className="rounded-full p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500"
                aria-label="Close preview"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </DialogHeader>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-65px)]">
              {formData && (
                <JobOfferCard
                  jobOffer={{
                    ...previewOffer,
                    schedules: formData.schedules.map((schedule, index) => ({
                      ...schedule,
                      id: `temp-${index}`,
                    })),
                  }}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Main content with responsive layout */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Form section - takes full width on mobile, partial on desktop */}
        <div className="w-full lg:w-7/12">
          <JobOfferForm
            defaultValues={formData}
            onSave={handleSave}
            onChange={handleFormUpdate}
            onCancel={closeDestination}
          />
        </div>

        {/* Desktop Preview section - hidden on mobile */}
        <div className="hidden lg:block lg:w-5/12 sticky top-6 self-start">
          <div className="border rounded-lg shadow-xs overflow-auto max-h-[calc(100vh-120px)]">
            <div className="p-4 bg-gray-50 border-b flex justify-between items-center">
              <h2 className="font-semibold text-gray-800">Preview</h2>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                Live
              </span>
            </div>
            <div className="p-6">
              {formData && (
                <JobOfferCard
                  jobOffer={{
                    ...previewOffer,
                    schedules: formData.schedules.map((schedule, index) => ({
                      ...schedule,
                      id: `temp-${index}`,
                    })),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
